import { API_RESORT_DETAILS } from '../core/redux/api/endpoints';
import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../core/redux/IAppState';
import { ACTION_TYPE } from '../core/redux/actionType';
import { hasTrippedBreaker, updateBreaker } from '../core/redux/api/circuitBreaker/module';
import * as R from 'ramda';
import { Resort } from './resort';
import ImmutableMap from '../language/ImmutableMap';
import { assoc } from '../language/module';
import {getLoading} from '../core/redux/api/module';
import { getResource } from '../language/resources/getResource';


const KEY_ACTION = ACTION_TYPE.RESORT_DETAILS_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.RESORT_DETAILS_REQUEST,
  ACTION_TYPE.RESORT_DETAILS_RESPONSE,
  ACTION_TYPE.RESORT_DETAILS_ERROR,
];


/**
 * Constructs a redux action that can be dispatched to fetch information about the IKON Ski Resorts. This includes
 * details like which resort we're at.
 */
export const getResortDetailsAction = () => makeApiAction(
  {
    method:   'GET',
    endpoint: API_RESORT_DETAILS,
    headers:  (state: IAppState) => ({
      'content-type':  'application/json',
      'Authorization': `Bearer ${state.auth.bearerToken}`,
    }),
    types:    ACTIONS as RsaaTypes,
    bailout:  state => !!state.resortDetails // If we've already loaded resort details
                       || !!getLoading(state, 'isLoadingResorts', KEY_ACTION) // If we're loading resort details
                       || hasTrippedBreaker(state, KEY_ACTION), // If we've tripped the breaker
  },
);


const reducer: ApiReducer<Resort[]> = (state, action, error, isLoading, payload) => {
  if (isLoading) {
    return R.assocPath([ 'resortDetails', 'isLoadingResorts' ], true, state);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return R.assocPath([ 'resortDetails', 'resortLoadError' ], error, withBreaker);
  }

  if (payload?.length === 0) {
    console.error(getResource(state.locale, 'GET_RESORT_DETAILS_ACTION_ERROR'), payload);
  }

  return assoc(
    'resortDetails',
    {
      isLoadingResorts: false,
      resortLoadError:  undefined,
      homeResort:       (payload || []).find(resort => resort.isHomeResort)!,
      allResorts:       (payload || []).reduce(
        (resorts, resort) => resorts.add(resort.resortAbbreviation, resort),
        ImmutableMap.empty<Resort>(),
      ),
    },
    withBreaker,
  );
};


/**
 * Reduces out a new app state from resort details actions. These actions load information about all Ikon resorts and
 * our current resort.
 */
export const getResortDetailsReducer = makeApiReducer<Resort[]>(ACTIONS, KEY_ACTION, reducer);
